/**
 * The read-only article page a chat citation opens (`/knowledge/read/:id`, 9
 * Oct): any signed-in person, a published article only, and an "IT only" one
 * only for IT — the server decides and answers 404 otherwise. A nudge back
 * to the chat sits under it: if the guide did not help, the assistant logs a
 * ticket.
 */

import { Badge, Button, Card, Typography } from "@norican/react";
import { Link, useParams } from "react-router";

import {
  AppBreadcrumbs,
  ErrorState,
  LoadingRows,
  Markdown,
  PageContainer,
  RelativeTime,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";

import { useReadKnowledgeArticle } from "./api";

export function KnowledgeReadPage() {
  const { t } = useT();
  const { articleId } = useParams();
  const article = useReadKnowledgeArticle(articleId);
  useDocumentTitle(article.data?.title ?? t("knowledgeAdmin.read.title"));
  const crumbs = <AppBreadcrumbs items={[{ label: t("knowledgeAdmin.read.title") }]} />;

  if (article.isLoading) {
    return (
      <PageContainer breadcrumbs={crumbs}>
        <LoadingRows rows={6} />
      </PageContainer>
    );
  }
  if (article.error != null || article.data === undefined) {
    return (
      <PageContainer breadcrumbs={crumbs}>
        <ErrorState error={article.error} title={t("knowledgeAdmin.read.missing")} />
      </PageContainer>
    );
  }
  const data = article.data;
  return (
    <PageContainer title={data.title} subtitle={data.summary ?? undefined} breadcrumbs={crumbs}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          {(data.area_names ?? []).map((name) => (
            <Badge key={name} tone="accent" variant="subtle" size="sm">
              {name}
            </Badge>
          ))}
          {data.audience === "it" ? (
            <Badge tone="accent" variant="outline" size="sm">
              {t("knowledgeAdmin.audience.it")}
            </Badge>
          ) : null}
          <Typography variant="caption" tone="muted">
            {t("knowledgeAdmin.read.updated")}{" "}
            <RelativeTime value={data.updated_at} withTime={false} />
          </Typography>
        </div>
        <Card variant="outline" padding="lg">
          <Markdown>{data.body_markdown}</Markdown>
        </Card>
        <Card variant="subtle" padding="md" className="flex flex-wrap items-center gap-3">
          <Typography variant="body-sm">{t("knowledgeAdmin.read.didntHelp")}</Typography>
          <Button size="sm" render={<Link to="/ask" />}>
            {t("knowledgeAdmin.read.backToChat")}
          </Button>
        </Card>
      </div>
    </PageContainer>
  );
}
